/*
 * Catching Zerodha's request token, whatever path it lands on.
 *
 * THE BUG THIS EXISTS TO PREVENT, because it was a real one and it is silent.
 *
 * Zerodha sends the browser back to whatever redirect URL is registered on the
 * Kite Connect app, with `?request_token=…` on the end. That URL is configured
 * at Zerodha and not here, so this application does not get to choose the path
 * — it was `/algo/` from when the tool lived on tools.hkmjerp.in, and there is
 * no reason to assume it will be `/` tomorrow either.
 *
 * The token was previously read inside an effect on the dashboard. Two things
 * went wrong with that, together:
 *
 *   * `/algo/` matches no route, so React Router's catch-all redirected to `/`
 *     — and `<Navigate to="/">` does not carry the query string with it.
 *   * That redirect happens in an effect deep in the tree, and child effects
 *     run before parent ones. So the URL was already rewritten by the time
 *     anything looked at it.
 *
 * The result was a person signing in to Zerodha successfully, being returned
 * here, and being told to sign in to Zerodha. Nothing errored; the token was
 * simply gone.
 *
 * THE FIX. Read it at module load — when this file is first evaluated, which
 * is before React has rendered a single element and therefore before any
 * redirect, route match or effect can touch the URL. It is stashed here, taken
 * out of the address bar immediately so a reload cannot try to spend it twice,
 * and handed over exactly once.
 */

/** Zerodha's own words for a login that did not work. */
function failureFrom(params: URLSearchParams): string | null {
  const status = params.get("status");
  if (status && status !== "success") {
    return params.get("message") || "The Zerodha sign-in did not complete.";
  }
  return null;
}

function capture(): { token: string | null; error: string | null } {
  if (typeof window === "undefined") return { token: null, error: null };

  const params = new URLSearchParams(window.location.search);
  const error = failureFrom(params);
  const token = error ? null : params.get("request_token");

  if (token || error) {
    // Out of the address bar at once. A request token is good for one exchange
    // and a few minutes, so a reload that tried to spend it again would show a
    // failure for something that had actually worked.
    //
    // The path is normalised to "/" at the same time: Zerodha may have sent the
    // browser to /algo/, which is not a route this application has, and leaving
    // it there would land the person on the catch-all redirect instead of the
    // dashboard.
    window.history.replaceState({}, "", "/");
  }

  return { token, error };
}

const captured = capture();

let taken = false;

/**
 * The token, once. Returns null on every call after the first.
 *
 * Once, because React 19's StrictMode mounts every component twice in
 * development and a token spent twice is a token Zerodha rejects the second
 * time — which would put a failure toast on a screen where nothing failed.
 */
export function takeRequestToken(): string | null {
  if (taken) return null;
  taken = true;
  return captured.token;
}

/** A sign-in Zerodha itself refused, if that is what came back. */
export function loginFailure(): string | null {
  return captured.error;
}
